import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import EventHistoryModal from "../../components/modals/EventHistoryModal";
import { EventHistoryResult } from "../../components/modals/EventHistoryModal";

// Types matching the profile structure
export interface ProfileData {
    uusrId: number;
    uusrName: string;
    uusrEmail: string;
    uusrPhoneNumber: string;
    uusrGender: number;
    uusrBloodType: string;
    uusrNationality: string;
    uusrIdentityType: number;
    uusrIdentityNumber: string;
    uusrDateOfBirth: string;
    uusrOccupation: string;
    uusrCommunity: string;
}

export interface ProfileProps {
    data?: ProfileData;
    eventHistoryData?: EventHistoryResult;
    isLoading?: boolean;
    error?: Error | null;
    eventHistory?: EventHistoryResult;
    isEventHistoryLoading?: boolean;
    eventHistoryError?: Error | null;
    onLogout: () => void;
}

// Skeleton loader component for loading states
const SkeletonCard: React.FC<{ className?: string }> = ({ className = "" }) => (
    <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

// Avatar component with initials fallback
const UserAvatar: React.FC<{ name: string; className?: string }> = ({ name, className = "" }) => {
    const initials = name
        .split(" ")
        .map(n => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);

    return (
        <div
            className={`
                relative flex items-center justify-center 
                bg-gradient-to-br from-brand-blue to-brand-blue/80
                text-white font-bold rounded-full
                transition-transform duration-300 hover:scale-105
                ${className}
            `}
            aria-label={`Avatar for ${name}`}
        >
            <span className="text-2xl md:text-3xl font-evil tracking-wider">{initials}</span>
        </div>
    );
};

// Info card component for personal details
const InfoCard: React.FC<{
    label: string;
    value: string;
    icon?: React.ReactNode;
    delay?: number;
}> = ({ label, value, icon, delay = 0 }) => (
    <div
        className="
            group bg-white rounded-xl p-5 
            border border-gray-100 shadow-sm
            hover:shadow-lg hover:border-brand-blue/20
            transition-all duration-300 ease-out
            transform hover:-translate-y-1
        "
        style={{ animationDelay: `${delay}ms` }}
    >
        <div className="flex items-start gap-3">
            {icon && (
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-brand-blue/10 flex items-center justify-center text-brand-blue group-hover:bg-brand-blue group-hover:text-white transition-colors duration-300">
                    {icon}
                </div>
            )}
            <div className="flex-1 min-w-0 font-poppins">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    {label}
                </p>
                <p className="text-sm md:text-base font-semibold text-brand-blue truncate">
                    {value || "-"}
                </p>
            </div>
        </div>
    </div>
);

// Main Profile Component
const Profile: React.FC<ProfileProps> = ({
    data,
    isLoading = false,
    error = null,
    eventHistory,
    isEventHistoryLoading = false,
    eventHistoryError = null,
    onLogout,
}) => {
    const { t } = useTranslation();
    const [isEventHistoryModalOpen, setIsEventHistoryModalOpen] = useState(false);

    // Check if user has any pending or paid event registration
    const hasPendingOrPaidEvent = React.useMemo(() => {
        const historyData = eventHistory?.data || [];
        return historyData.some(
            (item) =>
                item.uuehTrnsStatusLabel === "paid" ||
                item.uuehTrnsStatusLabel === "pending"
        );
    }, [eventHistory]);

    // Helper functions to get display values
    const getGenderLabel = (gender: number): string => {
        return t(`profile.gender.${gender}`, { defaultValue: t("profile.gender.1") });
    };

    const getIdentityTypeLabel = (type: number): string => {
        return t(`profile.identityType.${type}`, { defaultValue: t("profile.identityType.1") });
    };

    const formatPhoneNumber = (phone: string): string => {
        if (!phone) return "-";
        if (phone.startsWith("62")) {
            return `+${phone.slice(0, 2)} ${phone.slice(2, 5)}-${phone.slice(5, 9)}-${phone.slice(9)}`;
        }
        return phone;
    };

    // Icons for info cards
    const icons = {
        user: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
        ),
        email: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        ),
        phone: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
        ),
        gender: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
        ),
        blood: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
        ),
        flag: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-8a2 2 0 012-2h14a2 2 0 012 2v8M3 21h18M5 11V7a2 2 0 012-2h14a2 2 0 012 2v4M5 11h14" />
            </svg>
        ),
        id: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
            </svg>
        ),
        calendar: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
        ),
        work: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        ),
        users: (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
        ),
    };

    if (isLoading) {
        return (
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Skeleton Header */}
                <div className="flex flex-col md:flex-row items-center gap-6 mb-10">
                    <SkeletonCard className="w-24 h-24 md:w-32 md:h-32 rounded-full" />
                    <div className="flex-1 w-full">
                        <SkeletonCard className="h-8 w-3/4 mb-3" />
                        <SkeletonCard className="h-4 w-1/2" />
                    </div>
                </div>

                {/* Skeleton Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {[...Array(9)].map((_, i) => (
                        <SkeletonCard key={i} className="h-24" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center font-poppins">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-red-800 mb-2">
                        {t("profile.loading.error", "Failed to load profile data")}
                    </h3>
                    <p className="text-red-600">{error.message}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center font-poppins">
                    <p className="text-gray-500">{t("profile.loading.title", "Loading profile...")}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
            {/* Profile Header with Avatar */}
            <section
                className="mb-10 md:mb-14"
                aria-labelledby="profile-heading"
            >
                <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8">
                    <UserAvatar
                        name={data.uusrName}
                        className="w-24 h-24 md:w-32 md:h-32 shadow-2xl"
                    />
                    <div className="text-center md:text-left flex-1 font-poppins">
                        <h1
                            id="profile-heading"
                            className="text-3xl md:text-4xl lg:text-5xl font-evil text-brand-blue tracking-wide mb-2"
                        >
                            {data.uusrName}
                        </h1>
                        <p className="text-gray-500 text-sm md:text-base mb-4">
                            {data.uusrEmail}
                        </p>
                        <div className="flex flex-wrap justify-center md:justify-start gap-3">
                            <button
                                className={`
                                    px-6 py-2.5 bg-brand-blue text-white font-semibold rounded-lg
                                    transition-all duration-300
                                    ${hasPendingOrPaidEvent
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:bg-brand-blue/90 hover:shadow-lg transform hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2"
                                    }
                                `}
                                aria-label={t("profile.actions.registerEvent")}
                                onClick={() => {
                                    if (!hasPendingOrPaidEvent) {
                                        window.location.href = "/registration/form";
                                    }
                                }}
                                disabled={hasPendingOrPaidEvent}
                                title={hasPendingOrPaidEvent ? t("profile.actions.registerEventDisabled", "You have an active registration") : undefined}
                            >
                                {t("profile.actions.registerEvent")}
                            </button>
                            <button
                                className="
                                    px-6 py-2.5 bg-brand-blue text-white font-semibold rounded-lg
                                    hover:bg-brand-blue/90 hover:shadow-lg
                                    focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2
                                    transition-all duration-300 transform hover:-translate-y-0.5
                                "
                                aria-label={t("profile.actions.eventHistory")}
                                onClick={() => setIsEventHistoryModalOpen(true)}
                            >
                                {t("profile.actions.eventHistory")}
                            </button>
                            <button
                                className="
                                    px-6 py-2.5 bg-red-600 text-white font-semibold rounded-lg
                                    hover:bg-red-700 hover:shadow-lg
                                    focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                                    transition-all duration-300 transform hover:-translate-y-0.5
                                "
                                aria-label="Logout"
                                onClick={onLogout}
                            >
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Personal Information */}
            <section className="mb-10 md:mb-14" aria-labelledby="personal-heading">
                <h2
                    id="personal-heading"
                    className="text-2xl md:text-3xl font-evil text-brand-blue tracking-wide mb-6 flex items-center gap-2"
                >
                    <span className="w-1.5 h-8 bg-brand-orange rounded-full"></span>
                    {t("profile.sections.personalInfo")}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    <InfoCard
                        label={t("profile.fields.uusrName")}
                        value={data.uusrName}
                        icon={icons.user}
                        delay={0}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrEmail")}
                        value={data.uusrEmail}
                        icon={icons.email}
                        delay={50}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrPhoneNumber")}
                        value={formatPhoneNumber(data.uusrPhoneNumber)}
                        icon={icons.phone}
                        delay={100}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrGender")}
                        value={getGenderLabel(data.uusrGender)}
                        icon={icons.gender}
                        delay={150}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrBloodType")}
                        value={data.uusrBloodType}
                        icon={icons.blood}
                        delay={200}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrNationality")}
                        value={data.uusrNationality === "ID" ? "Indonesia" : data.uusrNationality}
                        icon={icons.flag}
                        delay={250}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrIdentityType")}
                        value={getIdentityTypeLabel(data.uusrIdentityType)}
                        icon={icons.id}
                        delay={300}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrIdentityNumber")}
                        value={data.uusrIdentityNumber}
                        icon={icons.id}
                        delay={350}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrDateOfBirth")}
                        value={data.uusrDateOfBirth}
                        icon={icons.calendar}
                        delay={400}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrOccupation")}
                        value={data.uusrOccupation}
                        icon={icons.work}
                        delay={450}
                    />
                    <InfoCard
                        label={t("profile.fields.uusrCommunity")}
                        value={data.uusrCommunity}
                        icon={icons.users}
                        delay={500}
                    />
                </div>
            </section>

            {/* Event History Modal */}
            <EventHistoryModal
                isOpen={isEventHistoryModalOpen}
                onClose={() => setIsEventHistoryModalOpen(false)}
                eventHistory={eventHistory}
                isLoading={isEventHistoryLoading}
                error={eventHistoryError}
            />
        </div>
    );
};

export default Profile;
